import NotFoundState from '@rio-cloud/rio-uikit/NotFoundState';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCardOrders } from '@/api/cardOrders';
import { filterOrdersBySearch } from '@/api/cardOrders/utils';
import TableFilter from '@/components/Table/TableFilter';
import { FeatureToggles } from '@/configuration/featureToggle/featureToggles';
import CustomExpanderPanel from '@/features/chargingCards/history/CustomExpanderPanel';
import OrderBody from '@/features/chargingCards/history/OrderBody';
import OrderBodyDetails from '@/features/chargingCards/history/OrderBodyDetails';
import OrderHeader from '@/features/chargingCards/history/OrderHeader';
import OrderHistoryLoader from '@/features/chargingCards/history/OrderHistoryLoader';
import OrderHistorySummary from '@/features/chargingCards/history/OrderHistorySummary';
import OrderStatusFilter, {
    ORDER_STATUS_KEYS,
    type OrderStatusKey,
} from '@/features/chargingCards/history/OrderStatusFilter';
import { useFeatureToggle } from '@/hooks/useFeatureToggle';
import { t } from '@/lib/utils';

const OrderHistory = () => {
    const { locale } = useIntl();
    const { data, isLoading, isError, refetch } = useCardOrders();
    const { value: isCardOrderHistoryEnabled } = useFeatureToggle(FeatureToggles.ENABLE_CARD_ORDER_HISTORY);

    const [searchValue, setSearchValue] = useState('');
    // All options are selected by default. Local state is reset on every mount
    const [selectedStatuses, setSelectedStatuses] = useState<OrderStatusKey[]>([...ORDER_STATUS_KEYS]);

    const renderFilters = () => (
        <div className='display-flex align-items-end width-100pct margin-bottom-20'>
            <div style={{ width: 'calc((100% - 60px) / 4)' }}>
                <TableFilter
                    id='orderHistorySearch'
                    filter={searchValue}
                    setFilter={setSearchValue}
                    searchByLabel={t('orderHistory.filter.searchBy')}
                    wrapperClassName='width-100pct'
                />
            </div>
            <div className='form-group margin-bottom-0 margin-left-20'>
                <label htmlFor='order-status-filter' className='display-block'>
                    {t('orderHistory.orderStatus')}
                </label>
                <OrderStatusFilter value={selectedStatuses} onChange={setSelectedStatuses} />
            </div>
        </div>
    );

    const renderOrders = () => {
        if (!data?.cardOrderHistory || data.cardOrderHistory.length === 0) {
            return (
                <NotFoundState
                    headline={t('orderHistory.errorMessage.noOrdersFound.title')}
                    message={t('orderHistory.errorMessage.noOrdersFound.description')}
                    fullWidth={true}
                />
            );
        }

        const searchedOrders = filterOrdersBySearch(data.cardOrderHistory, searchValue);
        const filteredOrders = searchedOrders.filter(order =>
            selectedStatuses.includes(order.cardStatus as OrderStatusKey)
        );

        return (
            <>
                {data.historySummary && isCardOrderHistoryEnabled ? (
                    <OrderHistorySummary summary={data.historySummary} />
                ) : null}
                {isCardOrderHistoryEnabled ? renderFilters() : null}
                {filteredOrders.length === 0 ? (
                    <NotFoundState
                        headline={t('orderHistory.errorMessage.noOrdersFound.title')}
                        message={t('orderHistory.filter.noResults.description')}
                        fullWidth={true}
                    />
                ) : (
                    filteredOrders.map(order => (
                        <CustomExpanderPanel
                            key={order.orderId}
                            header={<OrderHeader orderId={order.orderId} />}
                            body={<OrderBody order={order} locale={locale} />}
                            bodyDetails={<OrderBodyDetails order={order} locale={locale} />}
                        />
                    ))
                )}
            </>
        );
    };

    if (isError) {
        return (
            <NotFoundState
                headline={t('orderHistory.errorMessage.unavailable.title')}
                message={t('orderHistory.errorMessage.unavailable.description')}
                buttons={[{ text: t('general.tryAgain'), onClick: () => refetch() }]}
                fullWidth={true}
            />
        );
    }

    return (
        <>
            {isLoading ? (
                <OrderHistoryLoader
                    showSummary={Boolean(isCardOrderHistoryEnabled)}
                    showFilter={Boolean(isCardOrderHistoryEnabled)}
                />
            ) : (
                renderOrders()
            )}
        </>
    );
};

export default OrderHistory;
